// docs/189 EO: the things gallery (`?gallery=1&things=1`): every kind and every variant of the objects atlas laid
// out on one board for review — a lamp lit and put out, a few flammable things burning — with a wall along the top
// for the things that hang on walls. Pure: the Gallery draws what this returns.
import type { ThingKind, ThingView, Tile } from '../../core/sim/types';
import { variantOf } from '../../core/matter/shapes';
import type { ObjectTable } from './objectArt';

export interface Showroom { w: number; h: number; walls: Tile[]; things: ThingView[]; labels: Array<{ tile: Tile; text: string }>; heroes: Tile[] }

/** Things shown burning as well, to judge the fire over a big footprint. */
const BURNING: readonly string[] = ['Haystack', 'Wagon', 'CoveredWagon', 'Table', 'Rug', 'Bed', 'MarketStall', 'Tapestry', 'Pavilion', 'Barricade'];

/** An id whose variant (`variantOf`) is `v` of `n`, above `from`: the gallery shows a chosen variant. */
export function idFor(v: number, n: number, from: number): number {
  for (let id = from; ; id++) if (variantOf(id, n) === v) return id;
}

export function showroom(table: ObjectTable, opts: { width?: number; lamps?: ReadonlySet<string>; flammable?: ReadonlySet<string> } = {}): Showroom {
  const W = opts.width ?? 44;
  const things: ThingView[] = [];
  const labels: Showroom['labels'] = [];
  const heroes: Tile[] = [];
  let id = 1;
  const add = (kind: string, tile: Tile, v: number, n: number, lit: boolean, burn = 0) => {
    id = idFor(v, n, id);
    things.push({ id, tile, kind: kind as ThingKind, lit, burn, spent: false, name: kind });
    id += 1;
  };
  const kinds = Object.keys(table).filter((k) => !k.startsWith('task_')).sort();
  // the wall things along the top wall, floor beneath
  const walls: Tile[] = [];
  for (let x = 0; x < W; x++) walls.push({ x, y: 0 });
  let wx = 1;
  for (const k of kinds.filter((k) => table[k]!.view === 'wall')) {
    const n = table[k]!.variants.length;
    for (let v = 0; v < n && wx < W - 1; v++, wx += 2) {
      add(k, { x: wx, y: 0 }, v, n, false);
      if (v === 0) labels.push({ tile: { x: wx, y: 1 }, text: k });
    }
  }
  // the rest in shelves: two rows of headroom, the footprints, a row for the labels
  const rest = [...kinds.filter((k) => table[k]!.view !== 'wall'), ...Object.keys(table).filter((k) => k.startsWith('task_')).sort()];
  let x = 1, y = 3, shelf = 1;
  const place = (fw: number, fh: number): Tile => {
    if (x + fw > W - 1) { x = 1; y += 2 + shelf + 1; shelf = 1; }
    const t = { x, y };
    x += fw + 1;
    shelf = Math.max(shelf, fh);
    return t;
  };
  for (const k of rest) {
    const e = table[k]!;
    const [fw, fh] = e.fp;
    const n = e.variants.length;
    const lamp = opts.lamps?.has(k) ?? false;
    let first = true;
    for (let v = 0; v < n; v++) {
      const t = place(fw, fh);
      add(k, t, v, n, lamp);
      if (first) labels.push({ tile: { x: t.x, y: t.y + fh }, text: k });
      first = false;
    }
    if (lamp) add(k, place(fw, fh), 0, n, false);
    if (BURNING.includes(k) && (opts.flammable?.has(k) ?? true)) add(k, place(fw, fh), 0, n, true, 2);
    // a hero beside every few kinds, for scale
    if (things.length % 9 === 0) heroes.push(place(1, 1));
  }
  const h = y + shelf + 2;
  return { w: W, h, walls, things, labels, heroes };
}
